// Intake Document Requirements Configuration
// Used by both Broker Portal (BusinessIntakeDocuments) and Borrower Portal (BorrowerIntakeDocuments)

/**
 * Business Intake Documents - Required for prequalified deals in Broker Portal
 * Storage path: prequalifications/{brokerId}/{prequalId}/intake/{documentName}/{label}/{fileName}
 */
export const businessIntakeDocuments = [
  // Required documents (always shown)
  {
    name: 'Business Debt Schedule',
    description: 'Complete debt schedule for the business',
    required: true,
    conditional: false,
    labels: ['Business Debt Schedule'],
    validationNote: 'Must match interim balance sheet'
  },
  {
    name: 'Articles of Organization/Incorporation',
    description: 'Articles of Organization (LLC) or Articles of Incorporation (corporation)',
    required: true,
    conditional: false,
    labels: ['Articles of Organization', 'Articles of Incorporation']
  },
  {
    name: 'Operating Agreement/Corporate By-Laws',
    description: 'Operating agreement (LLC) or corporate by-laws (corporation)',
    required: true,
    conditional: false,
    labels: ['Operating Agreement', 'Corporate By-Laws']
  },
  {
    name: 'AR/AP Aging Report',
    description: '2025 interim AR/AP aging report',
    required: true,
    conditional: false,
    labels: ['2025 Interim AR/AP Aging Report'],
    validationNote: 'Must match date of interim balance sheet'
  },
  // Conditional documents (shown with checkbox toggles)
  {
    name: 'Assumed/Fictitious Name/DBA Filing',
    description: 'Assumed name, fictitious name, or DBA filing',
    required: false,
    conditional: true,
    conditionKey: 'hasAssumedName',
    conditionLabel: 'Business operates under an assumed name, fictitious name, or DBA',
    labels: ['DBA Filing']
  },
  {
    name: 'Franchise Documents',
    description: 'FDD and Franchise Agreement',
    required: false,
    conditional: true,
    conditionKey: 'isFranchise',
    conditionLabel: 'Business is a franchise',
    labels: ['FDD (Franchise Disclosure Document)', 'Franchise Agreement']
  },
  {
    name: 'IRS Tax ID Letter',
    description: 'IRS Tax ID Letter (EIN confirmation)',
    required: false,
    conditional: true,
    conditionKey: 'noHistoricalTaxReturns',
    conditionLabel: 'No historical tax returns available',
    labels: ['IRS Tax ID Letter']
  },
  {
    name: 'Lease Documents',
    description: 'Lease agreement and amendments/LOI',
    required: false,
    conditional: true,
    conditionKey: 'hasLeaseholdImprovements',
    conditionLabel: 'Leasehold improvements are part of the transaction',
    labels: ['Lease Agreement', 'Lease Amendments', 'LOI (Letter of Intent)']
  },
  {
    name: 'Vehicle List',
    description: 'List of vehicles used as collateral',
    required: false,
    conditional: true,
    conditionKey: 'hasVehicleCollateral',
    conditionLabel: 'Vehicles are being used as collateral',
    labels: ['Vehicle List'],
    validationNote: 'Include VIN, year, make, model, value, and odometer reading for each vehicle'
  },
  {
    name: 'Debt Refinancing Documents',
    description: 'Documents for debt refinancing',
    required: false,
    conditional: true,
    conditionKey: 'hasDebtRefinancing',
    conditionLabel: 'Debt refinancing is part of the transaction',
    labels: ['Executed Notes', 'Security Agreements', 'Payment Transcripts', 'Payoff Letters']
  }
];

/**
 * Borrower Intake Documents - Required for prequalified borrowers in Customer Portal
 * Storage path: documents/{userId}/{loanApplicationId}/intake/{documentName}/{label}/{fileName}
 */
export const borrowerIntakeDocuments = [
  // Stage 2 documents — only exposed after engagement is signed and paid.
  {
    name: 'Government-Issued ID',
    description: 'Copy of a valid government-issued identification',
    required: true,
    conditional: false,
    individual: true,
    labels: ['Government-Issued ID']
  },
  {
    name: 'Personal Debt Schedule',
    description: 'List each personal debt, lender, outstanding balance, monthly payment, maturity, and collateral',
    required: true,
    conditional: false,
    individual: true,
    labels: ['Personal Debt Schedule']
  },
  {
    name: 'Source of Funds Documentation',
    description: 'Account statements and transfer trails documenting the source of the acquisition equity injection',
    required: true,
    conditional: false,
    individual: false,
    labels: ['Source of Funds Documentation'],
    validationNote: 'Include a clear trail for transfers between accounts and explain any large recent deposits'
  },
  {
    name: 'Business Returns for Owned Companies',
    description: 'Three years of complete federal returns for every business in which you own 20% or more',
    required: false,
    conditional: true,
    conditionKey: 'ownsOtherBusiness',
    conditionLabel: 'I own 20% or more of another business',
    individual: true,
    labels: ['Owned Business Return - Most Recent Year', 'Owned Business Return - Prior Year', 'Owned Business Return - Two Years Prior']
  },

  // Conditional documents (shown with checkbox toggles)
  {
    name: 'Spouse Information and Signature',
    description: 'Spouse information and signed Form 413 pages when ownership, joint assets, or community-property rules require it',
    required: false,
    conditional: true,
    conditionKey: 'spouseSignatureRequired',
    conditionLabel: 'My spouse will jointly own 20%+, is included on the PFS, or community-property rules apply',
    individual: true,
    labels: ['Spouse Information and Signature']
  },
  {
    name: 'Divorce or Support Documentation',
    description: 'Divorce decree and documentation of alimony, child support, or other continuing support obligations',
    required: false,
    conditional: true,
    conditionKey: 'hasDivorceOrSupportObligations',
    conditionLabel: 'I have divorce, alimony, or child-support obligations',
    individual: true,
    labels: ['Divorce Decree or Support Documentation']
  },
  {
    name: 'Acquisition Vehicle Entity Documents',
    description: 'Articles, EIN confirmation, and operating agreement for the acquisition entity once formed',
    required: false,
    conditional: true,
    conditionKey: 'acquisitionEntityFormed',
    conditionLabel: 'The acquisition entity has been formed',
    individual: false,
    labels: ['Articles of Organization', 'EIN Confirmation', 'Operating Agreement']
  },
  {
    name: 'Current Employment Income',
    description: 'Recent W-2s or paystubs to support the global cash-flow and transition plan',
    required: false,
    conditional: true,
    conditionKey: 'currentlyEmployed',
    conditionLabel: 'I am currently employed and receive W-2 or payroll income',
    individual: true,
    labels: ['W-2 or Recent Paystubs']
  },
  {
    name: 'Gift Documentation',
    description: 'Signed gift letter and evidence of the donor transfer for any gifted injection funds',
    required: false,
    conditional: true,
    conditionKey: 'hasGiftedFunds',
    conditionLabel: 'Some acquisition funds will be gifted',
    individual: false,
    labels: ['Gift Letter and Transfer Evidence']
  }
];

/**
 * Get filtered list of applicable business intake documents based on conditions
 * @param {Object} conditions - Object with condition keys as boolean values
 * @returns {Array} Filtered array of applicable documents
 */
export const getApplicableBusinessDocuments = (conditions = {}) => {
  return businessIntakeDocuments.filter(doc => {
    if (!doc.conditional) return true;
    return conditions[doc.conditionKey] === true;
  });
};

/**
 * Get filtered list of applicable borrower intake documents based on conditions
 * @param {Object} conditions - Object with condition keys as boolean values
 * @returns {Array} Filtered array of applicable documents
 */
export const getApplicableBorrowerDocuments = (conditions = {}) => {
  return borrowerIntakeDocuments.filter(doc => {
    if (!doc.conditional) return true;
    return conditions[doc.conditionKey] === true;
  });
};

/**
 * Get all conditional business documents (for rendering checkboxes)
 * @returns {Array} Array of conditional documents
 */
export const getConditionalBusinessDocuments = () => {
  return businessIntakeDocuments.filter(doc => doc.conditional);
};

/**
 * Get all conditional borrower documents (for rendering checkboxes)
 * @returns {Array} Array of conditional documents
 */
export const getConditionalBorrowerDocuments = () => {
  return borrowerIntakeDocuments.filter(doc => doc.conditional);
};

/**
 * Get all required (non-conditional) business documents
 * @returns {Array} Array of required documents
 */
export const getRequiredBusinessDocuments = () => {
  return businessIntakeDocuments.filter(doc => !doc.conditional);
};

/**
 * Get all required (non-conditional) borrower documents
 * @returns {Array} Array of required documents
 */
export const getRequiredBorrowerDocuments = () => {
  return borrowerIntakeDocuments.filter(doc => !doc.conditional);
};
